import React, { createContext, useContext, useState, useEffect } from 'react';

const B2BAuthContext = createContext();

export const B2BAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    // Check for existing session
    const savedUser = localStorage.getItem('b2b_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  // Generate real OTP
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Send OTP - in production, this would call WhatsApp API
  const sendOTP = async (phone) => {
    setPhoneNumber(phone);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Generate real OTP
    const realOTP = generateOTP();
    localStorage.setItem('mock_otp', realOTP);
    localStorage.setItem('mock_phone', phone);
    
    setOtpSent(true);
    // في الإنتاج، سيتم إرسال هذا الكود عبر واتساب
    console.log(`OTP sent to ${phone}: ${realOTP}`);
    alert(`تم إرسال كود التحقق إلى واتساب\nللتجربة، استخدم الكود: ${realOTP}`);
    
    return { success: true, message: 'تم إرسال الكود إلى الواتساب', otp: realOTP };
  };

  // Verify OTP
  const verifyOTP = async (otp) => {
    const storedOTP = localStorage.getItem('mock_otp');
    const storedPhone = localStorage.getItem('mock_phone');
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (otp === storedOTP) {
      // Check if user exists
      const existingUsers = JSON.parse(localStorage.getItem('b2b_users') || '[]');
      const existingUser = existingUsers.find(u => u.phone === storedPhone);
      
      if (existingUser) {
        setUser(existingUser);
        localStorage.setItem('b2b_user', JSON.stringify(existingUser));
        return { success: true, isNewUser: false, user: existingUser };
      } else {
        return { success: true, isNewUser: true, phone: storedPhone };
      }
    } else {
      return { success: false, message: 'الكود غير صحيح. يرجى المحاولة مرة أخرى' };
    }
  };

  // Register new user
  const registerUser = async (userData) => {
    const newUser = {
      id: Date.now().toString(),
      ...userData,
      createdAt: new Date().toISOString()
    };
    
    // Save to mock database
    const existingUsers = JSON.parse(localStorage.getItem('b2b_users') || '[]');
    existingUsers.push(newUser);
    localStorage.setItem('b2b_users', JSON.stringify(existingUsers));
    
    // Set current user
    setUser(newUser);
    localStorage.setItem('b2b_user', JSON.stringify(newUser));
    
    return { success: true, user: newUser };
  };

  // Update user
  const updateUser = async (userData) => {
    const updatedUser = { ...user, ...userData };
    
    // Update in mock database
    const existingUsers = JSON.parse(localStorage.getItem('b2b_users') || '[]');
    const userIndex = existingUsers.findIndex(u => u.id === user.id);
    if (userIndex !== -1) {
      existingUsers[userIndex] = updatedUser;
      localStorage.setItem('b2b_users', JSON.stringify(existingUsers));
    }
    
    setUser(updatedUser);
    localStorage.setItem('b2b_user', JSON.stringify(updatedUser));
    
    return { success: true, user: updatedUser };
  };

  // Logout
  const logout = () => {
    setUser(null);
    setOtpSent(false);
    setPhoneNumber('');
    localStorage.removeItem('b2b_user');
    localStorage.removeItem('mock_otp');
    localStorage.removeItem('mock_phone');
  };

  return (
    <B2BAuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      otpSent,
      phoneNumber,
      sendOTP,
      verifyOTP,
      registerUser,
      updateUser,
      logout,
      setOtpSent
    }}>
      {children}
    </B2BAuthContext.Provider>
  );
};

export const useB2BAuth = () => {
  const context = useContext(B2BAuthContext);
  if (!context) {
    throw new Error('useB2BAuth must be used within a B2BAuthProvider');
  }
  return context;
};
